import { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Send } from "lucide-react";
import { useMutation } from "@apollo/client/react";
import { DOCUMENT_CHAT_HISTORY_MUTATION, DOCUMENT_ASSISTANT_MUTATION } from "../graphql/mutations";
import Markdown from "react-markdown";

export type EntityContext = {
  entityType: string;
  entityId?: string;
  entityName?: string;
};

type ChatbotProps = {
  context?: EntityContext; // Context data about the entity being viewed (must include entityId and entityType)
};

type Message = {
  id: number;
  role: "user" | "assistant";
  text: string;
};

const Chatbot: React.FC<ChatbotProps> = ({ context }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [fetchChatHistory, { loading: historyLoading }] = useMutation(DOCUMENT_CHAT_HISTORY_MUTATION);
  const [sendMessage, { loading: sendingMessage }] = useMutation(DOCUMENT_ASSISTANT_MUTATION);

  // Fetch chat history on mount
  useEffect(() => {
    if (context?.entityId && context?.entityType) {
      fetchChatHistory({
        variables: {
          input: {
            entityId: context.entityId,
            entityType: context.entityType,
          },
        },
      })
        .then((result: any) => {
          const data = result.data?.documentChatHistory;
          if (data) {
            // Transform API messages to component Message format
            const transformedMessages: Message[] = data.messages.map((msg: any, index: number) => ({
              id: index + 1,
              role: msg.role === "user" ? "user" : "assistant",
              text: msg.message || "",
            }));

            // If no messages, add default greeting
            if (transformedMessages.length === 0) {
              const entityName = context?.entityName || context?.entityType;
              transformedMessages.push({
                id: 1,
                role: "assistant",
                text: `Hello! I'm here to help you with ${entityName}. How can I assist you today?`,
              });
            }

            setMessages(transformedMessages);
            setSessionId(data.sessionId || null);
          }
        })
        .catch((error: any) => {
          console.error("Error fetching chat history:", error);
          // Fallback to default greeting on error
          const entityName = context?.entityName || context?.entityType || "this";
          setMessages([
            {
              id: 1,
              role: "assistant",
              text: `Hello! I'm here to help you with ${entityName}. How can I assist you today?`,
            },
          ]);
        });
    } else {
      // Fallback if no context provided
      setMessages([
        {
          id: 1,
          role: "assistant",
          text: "Hello! How can I help you today?",
        },
      ]);
    }
  }, [context?.entityId, context?.entityType, fetchChatHistory]);

  const MAX_INPUT_HEIGHT = 100; // Maximum height in pixels
  const LINE_HEIGHT = 24; // Approximate line height in pixels
  const MAX_ROWS = Math.floor(MAX_INPUT_HEIGHT / LINE_HEIGHT);

  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      
      // Set the height based on scrollHeight but cap at MAX_INPUT_HEIGHT
      const height = Math.min(scrollHeight, MAX_INPUT_HEIGHT);
      textarea.style.height = `${height}px`;
    }
  }, [input]);

  // Scroll to bottom when messages change or when sending message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, sendingMessage]);

  const handleSend = async () => {
    if (!input.trim() || !context?.entityId || !context?.entityType) return;

    const userMessage = input.trim();
    
    // Add user message immediately
    setMessages((prev) => [...prev, { id: Date.now(), role: "user", text: userMessage }]);
    setInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Prepare payload
    const payload = {
      prompt: userMessage,
      entityId: context.entityId,
      entityType: context.entityType,
      sessionId: sessionId,
    };

    try {
      const result = await sendMessage({
        variables: {
          input: payload,
        },
      });

      const responseData = (result.data as any)?.documentAssistant;
      
      if (responseData) {
        // Prepare response message object from response data
        const assistantMessage: Message = {
          id: Date.now(),
          role: "assistant",
          text: responseData.data
        };
        if(!sessionId){
          setSessionId(responseData.data?.sessionId || null);
        }
        // Add assistant response to messages
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Fallback if no response data
        const fallbackMessage: Message = {
          id: Date.now(),
          role: "assistant",
          text: "No response received. Please try again.",
        };
        setMessages((prev) => [...prev, fallbackMessage]);
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      // Add error message to chat
      const errorMessage: Message = {
        id: Date.now(),
        role: "assistant",
        text: error?.message || "Sorry, I encountered an error. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box className="p-3"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #EFEFEF",
        }}
      >
        <Typography variant="h2">
          Chatbot
        </Typography>
      </Box>

      {/* Messages */}
      <Stack className="p-2 chatbot-body"
        spacing={3}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          maxHeight: "100%",
          position: "relative",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            background: "#555",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
        }}
      >
        {historyLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
            <Typography variant="h6">
              Loading Chat...
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((msg) => (
            (msg.text?.trim() && (
              <Box
              key={msg.id}
              sx={{
                display: "flex",
                gap: 1,
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                flexDirection: msg.role === "user" ? "row-reverse" : "row",
              }}
            >
              <Box
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: msg.role === "assistant" ? "#eff5ff" : "#3b82f6",
                 
                }}
              >
                <Box
                  component="span"
                  className="material-symbols-outlined"
                  sx={{
                    fontSize: "16px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color:  msg.role === "assistant" ? "#000" : "#fff",
                  }}
                >
                  {msg.role === "assistant" ? "smart_toy" : "person"}
                </Box>
              </Box>
              <Box className="p-2"
                sx={{
                  borderRadius: "8px",
                  bgcolor: msg.role === "assistant" ? "#eff5ff" : "#3b82f6",
                  color:  msg.role === "assistant" ? "#000" : "#fff",
                  fontSize: "12px",
                  fontWeight: "400",
                }}
              >
                <Markdown>{msg.text}</Markdown>
              </Box>
            </Box>
            ))
          ))}
          {sendingMessage && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignSelf: "flex-start",
                flexDirection: "row",
              }}
            >
              <Box
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#eff5ff",
                }}
              >
                <Box
                  component="span"
                  className="material-symbols-outlined"
                  sx={{
                    fontSize: "16px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#000",
                  }}
                >
                  smart_toy
                </Box>
              </Box>
              <div className="">
            <div className="qts-message-typing">
              <div className="qts-typing-dot"></div>
              <div className="qts-typing-dot"></div>
              <div className="qts-typing-dot"></div>
            </div>
          </div>
              {/* <Box className="p-2"
                sx={{
                  borderRadius: "8px",
                  bgcolor: "#eff5ff",
                  color: "#000",
                  fontSize: "12px",
                  fontWeight: "400",
                }}
              >
                Thinking...
              </Box> */}
            </Box>
          )}
          <div ref={messagesEndRef} />
          </>
        )}
      </Stack>

      {/* Input */}
      <Box className="p-2"
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        sx={{
          borderTop: "1px solid #EFEFEF",
          display: "flex",
          gap: 2,
          alignItems: "flex-end",
        }}
      >
        <TextField
          inputRef={textareaRef}
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          multiline
          minRows={1}
          maxRows={MAX_ROWS}
          fullWidth
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              // bgcolor: "#fff",
              // color: "#fff",
              border: "1px solid #EFEFEF",
              borderRadius: "8px",
              maxHeight: `${MAX_INPUT_HEIGHT}px`,
              overflowY: "auto",
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
              "& textarea": {
                resize: "none",
                overflow: "hidden",
                lineHeight: `${LINE_HEIGHT}px`,
                padding: "8px 12px",
                "&::placeholder": {
                  color: "#9ca3af",
                  opacity: 1,
                },
              },
            },
          }}
        />
        <IconButton
          type="submit"
          aria-label="Send"
          size="medium"
          disabled={sendingMessage || !input.trim() || !context?.entityId || !context?.entityType}
          sx={{
            bgcolor: "#3b82f6",
            color: "#fff",
            "&:hover": { bgcolor: "#2563eb" },
            "&:disabled": { bgcolor: "#9ca3af", color: "#fff" },
            flexShrink: 0,
            mb: 0.5,
          }}
        >
          <Send size={16} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Chatbot;


